import { useMutation, useQuery } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'
import { createImage, listImages, deleteImage } from '@/lib/api/client'
import { uploadFile, DocumentTypeChoices } from '@/lib/api/client'
import { DocumentPage } from '@/lib/api/client'
import { truncate } from '@/lib/truncate'
import queryClient from '@/queryClient'

import {
  getImageTemplatesQuery,
  useCreateImageTemplate,
  useDeleteImageTemplate,
} from '@/services/hooks/template_attributes'
import MUTATION_KEYS from './mutationKeys'
import QUERY_KEYS from './queryKeys'
import CustomError from '@/lib/api/CustomError'
import { useDocumentState } from '@/state'
import { ImagePlaceholder } from '@/pages/DocumentPage/components/DocumentPageToolbar/DraggablePlaceholders'

/*
 ** Views in cache need to be updated after 2 minutes
 ** of inactivity to update the download URL
 */
const IMAGES_CACHE_TIME = 120000

export const useCreateImage = ({ onSuccess }: { onSuccess?: () => void }) => {
  const { mutateAsync: createImageTemplate } = useCreateImageTemplate()
  const { toast } = useToast()
  const startAddingElement = useDocumentState(
    (state) => state.startAddingElement,
  )

  return useMutation({
    mutationKey: [MUTATION_KEYS.CREATE_IMAGE],
    mutationFn: async ({
      documentPage,
      documentType,
      file,
      position,
    }: {
      documentPage: DocumentPage
      documentType?: DocumentTypeChoices
      file: File
      position: {
        clientX: number
        clientY: number
      }
    }) => {
      const isDocumentThemeEnabled = documentType === 'work_instructions'
      const documentPageId = documentPage.id
      if (!documentPageId) {
        throw new Error('No document ID found')
      }

      const documentVersionId = documentPage.document_version
      const image = await createImage(documentVersionId, {
        source_type: 'screenshot',
        name: truncate(file.name),
      })

      if (!image.upload_url || !image.id) {
        throw new Error('Unable to create image')
      }

      if (isDocumentThemeEnabled) {
        startAddingElement({
          newElementData: {
            Component: () => (
              <ImagePlaceholder src={image.download_url as string} />
            ),
            dataType: 'image',
            attributes: {
              imageId: image.id,
              imagePosition: 'absolute',
            },
            documentPageId,
          },
          triggerLocation: {
            x: position.clientX,
            y: position.clientY,
          },
        })
      } else {
        await createImageTemplate({
          documentPageId,
          imageData: {
            imageId: image.id,
            imagePosition: 'absolute',
          },
        })
      }

      await uploadFile(image.upload_url, file)

      return {
        documentPage,
        image,
      }
    },
    onSuccess: ({ documentPage, image }) => {
      queryClient.setQueriesData(
        {
          queryKey: [
            QUERY_KEYS.IMAGES,
            { documentVersionId: documentPage.document_version },
          ],
        },
        (images: Array<any> | undefined) => {
          if (images) {
            return [...images, image]
          }

          return [image]
        },
      )

      if (typeof onSuccess === 'function') {
        onSuccess()
      }
    },
    onError: (error: any) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}

export const useListImages = ({
  documentVersionId,
  enabled = true,
}: {
  documentVersionId?: string | null
  enabled?: boolean
}) => {
  const { toast } = useToast()
  return useQuery({
    queryKey: [QUERY_KEYS.IMAGES, { documentVersionId }],
    staleTime: IMAGES_CACHE_TIME,
    queryFn: () => {
      try {
        return documentVersionId ? listImages(documentVersionId) : []
      } catch (error) {
        console.error(error)
        if (error instanceof CustomError) {
          toast({
            title: 'Error',
            description: error.message,
            variant: 'destructive',
          })
        } else {
          toast({
            variant: 'destructive',
            title: 'Uh oh! Something went wrong.',
            description: 'There was a problem with fetching images.',
          })
        }
        return []
      }
    },
    enabled: !!documentVersionId && enabled,
  })
}

export const useDeleteImages = (props?: { onDelete?: () => void }) => {
  const { onDelete } = props || {}
  const { toast } = useToast()
  const { mutateAsync: deleteImageTemplate } = useDeleteImageTemplate()

  return useMutation({
    mutationKey: [MUTATION_KEYS.DELETE_IMAGE],
    mutationFn: async ({
      imageId,
      documentPage,
    }: {
      imageId: string
      documentPage: DocumentPage
    }) => {
      const documentPageId = documentPage.id
      if (!documentPageId) {
        return
      }

      await deleteImage(imageId)

      const imageTemplates = await getImageTemplatesQuery(documentPageId)
      const image = imageTemplates.find(
        (template) => template.template_values.imageId === imageId,
      )

      if (!image) return

      await deleteImageTemplate({
        templateAttributeId: image.id as string,
        documentPageId,
      })

      return { documentVersionId: documentPage.document_version }
    },
    onSuccess: async (documentVersionId) => {
      await queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.IMAGES,
          documentVersionId ? { documentVersionId } : {},
        ],
      })
      if (typeof onDelete === 'function') {
        onDelete()
      }
    },
    onError: (error: any) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}
